import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Search, SlidersHorizontal, Warehouse } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";

const EMPTY_ITEM = { name: "", type: "material", code: "", hsn: "", category: "", unit: "Nos",
  purchase_price: 0, sale_price: 0, tax_rate: 18, opening_stock: 0, min_stock: 0, description: "" };
const UNITS = ["Nos", "Kg", "Bag", "Box", "Mtr", "SqFt", "Ltr", "Day", "Job", "Set", "Pkt"];
const ITEM_TYPES = ["material", "product", "service", "labour", "composite"];

function ItemDialog({ open, onClose, item, onSaved, warehouses }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(item ? { ...EMPTY_ITEM, ...item } : EMPTY_ITEM); }, [item, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tracksStock = !["service", "labour"].includes(form.type);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Item name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, purchase_price: +form.purchase_price || 0, sale_price: +form.sale_price || 0,
        tax_rate: +form.tax_rate || 0, opening_stock: +form.opening_stock || 0, min_stock: +form.min_stock || 0 };
      if (item) await api.put(`/items/${item.id}`, payload);
      else await api.post("/items", payload);
      toast.success(item ? "Item updated" : "Item created");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="item-dialog">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Item Name *</Label><Input data-testid="item-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="item-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ITEM_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Item Code</Label><Input className="mt-1.5 font-mono" placeholder="Auto if empty" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} /></div>
          <div><Label>HSN / SAC</Label><Input data-testid="item-hsn" className="mt-1.5 font-mono" value={form.hsn} onChange={(e) => set("hsn", e.target.value)} /></div>
          <div><Label>Unit</Label>
            <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Purchase Price (₹)</Label><Input type="number" className="mt-1.5" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} /></div>
          <div><Label>Sale Price (₹)</Label><Input data-testid="item-sale-price" type="number" className="mt-1.5" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} /></div>
          <div><Label>GST Rate (%)</Label>
            <Select value={String(form.tax_rate)} onValueChange={(v) => set("tax_rate", +v)}>
              <SelectTrigger data-testid="item-tax-rate" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Category</Label><Input className="mt-1.5" value={form.category} onChange={(e) => set("category", e.target.value)} /></div>
          {tracksStock && !item && <div><Label>Opening Stock</Label><Input data-testid="item-opening-stock" type="number" className="mt-1.5" value={form.opening_stock} onChange={(e) => set("opening_stock", e.target.value)} /></div>}
          {tracksStock && <div><Label>Minimum Stock (alert level)</Label><Input type="number" className="mt-1.5" value={form.min_stock} onChange={(e) => set("min_stock", e.target.value)} /></div>}
          {tracksStock && (
            <div><Label>Warehouse</Label>
              <Select value={form.warehouse_id || "none"} onValueChange={(v) => set("warehouse_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="item-save-btn" onClick={save} disabled={busy}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Save Item"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Items() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [adjust, setAdjust] = useState({ open: false, item: null, qty: 0, reason: "" });
  const [whName, setWhName] = useState("");

  const load = () => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/items${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/warehouses").then((r) => setWarehouses(r.data.items || [])).catch(() => {});
  };
  useEffect(load, [activeId]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const doAdjust = async () => {
    try {
      const { data: r } = await api.post(`/items/${adjust.item.id}/adjust`, { qty: +adjust.qty, reason: adjust.reason });
      toast.success(`Stock updated to ${r.current_stock}`);
      setAdjust({ open: false, item: null, qty: 0, reason: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addWarehouse = async () => {
    if (!whName.trim()) return;
    try {
      await api.post("/warehouses", { name: whName });
      toast.success("Warehouse added");
      setWhName("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="items-page">
      <PageHeader title="Items & Inventory" testId="items-title" sub="Products, materials, services and stock levels">
        <Button data-testid="new-item-btn" onClick={() => setDialog({ open: true, item: null })}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Item</Button>
      </PageHeader>

      <Tabs defaultValue="items">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <TabsList>
            <TabsTrigger value="items" data-testid="items-tab"><Package className="mr-1.5 h-3.5 w-3.5" /> Items</TabsTrigger>
            <TabsTrigger value="warehouses" data-testid="warehouses-tab"><Warehouse className="mr-1.5 h-3.5 w-3.5" /> Warehouses</TabsTrigger>
          </TabsList>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="items-search" className="pl-9 bg-white dark:bg-slate-900" placeholder="Search name, code, HSN..."
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <TabsContent value="items">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            {!data ? <TableSkeleton /> : data.items.length === 0 ? (
              <EmptyState testId="items-empty" title="No items yet" body="Add products, materials or services to start billing." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="items-table">
                  <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="p-3">Item</th><th className="p-3">HSN</th><th className="p-3">Type</th>
                    <th className="p-3 text-right">Stock</th><th className="p-3 text-right">Sale Price</th>
                    <th className="p-3 text-right">GST</th><th className="p-3 text-right">Stock Value</th><th className="p-3"></th></tr></thead>
                  <tbody>
                    {data.items.map((it) => {
                      const low = it.min_stock > 0 && it.current_stock <= it.min_stock;
                      return (
                        <tr key={it.id} data-testid={`item-row-${it.code}`}
                          className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                          <td className="p-3">
                            <div className="font-medium">{it.name} {it.is_demo && <Badge variant="secondary" className="ml-1 text-[10px]">DEMO</Badge>}</div>
                            <p className="font-mono text-xs text-slate-500">{it.code}</p>
                          </td>
                          <td className="p-3 font-mono text-xs text-slate-500">{it.hsn || "—"}</td>
                          <td className="p-3 capitalize text-slate-500">{it.type}</td>
                          <td className="p-3 text-right">
                            {["service", "labour"].includes(it.type) ? <span className="text-slate-300">—</span> : (
                              <span className={`font-mono font-semibold ${low ? "text-red-600" : ""}`}>
                                {it.current_stock} <span className="text-xs font-normal text-slate-400">{it.unit}</span>
                                {low && <Badge className="ml-1.5 bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400 border-0 text-[10px]">LOW</Badge>}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right"><Money value={it.sale_price} /></td>
                          <td className="p-3 text-right font-mono text-xs">{it.tax_rate}%</td>
                          <td className="p-3 text-right"><Money value={(it.current_stock || 0) * (it.purchase_price || 0)} className="text-slate-500" /></td>
                          <td className="p-3">
                            <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                              <Button size="sm" variant="ghost" data-testid={`item-edit-${it.code}`} onClick={() => setDialog({ open: true, item: it })}>Edit</Button>
                              {!["service", "labour"].includes(it.type) && (
                                <Button size="sm" variant="ghost" data-testid={`item-adjust-${it.code}`}
                                  onClick={() => setAdjust({ open: true, item: it, qty: 0, reason: "" })}>
                                  <SlidersHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="warehouses">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <div className="flex gap-2 mb-4 max-w-md">
              <Input data-testid="warehouse-name" placeholder="New warehouse name" value={whName} onChange={(e) => setWhName(e.target.value)} />
              <Button data-testid="warehouse-add-btn" onClick={addWarehouse} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900 shrink-0">Add</Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="warehouses-list">
              {warehouses.map((w) => (
                <div key={w.id} className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
                  <p className="font-medium flex items-center gap-2"><Warehouse className="h-4 w-4 text-slate-400" /> {w.name}</p>
                  {w.address && <p className="text-xs text-slate-500 mt-1">{w.address}</p>}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ItemDialog open={dialog.open} item={dialog.item} warehouses={warehouses}
        onClose={() => setDialog({ open: false, item: null })} onSaved={load} />

      <Dialog open={adjust.open} onOpenChange={() => setAdjust({ open: false, item: null, qty: 0, reason: "" })}>
        <DialogContent aria-describedby={undefined} className="max-w-sm bg-white dark:bg-slate-900" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-display">Adjust Stock — {adjust.item?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Current stock: <b className="font-mono">{adjust.item?.current_stock} {adjust.item?.unit}</b></p>
          <div><Label>Quantity (+/-)</Label>
            <Input data-testid="adjust-qty" type="number" className="mt-1.5" value={adjust.qty}
              onChange={(e) => setAdjust((a) => ({ ...a, qty: e.target.value }))} /></div>
          <div><Label>Reason</Label>
            <Input data-testid="adjust-reason" className="mt-1.5" value={adjust.reason}
              onChange={(e) => setAdjust((a) => ({ ...a, reason: e.target.value }))} placeholder="Damage, correction, count..." /></div>
          <DialogFooter>
            <Button data-testid="adjust-save-btn" onClick={doAdjust} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Apply Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
